
import React from 'react';
import { ArrowLeft, Shield, Star, TrendingUp, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PremiumBot } from '@/types/premium-bots';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TourButton } from '@/components/common/TourButton';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedBotDetailHeaderProps {
  bot: PremiumBot | null;
  isLoading: boolean;
  isIntegrated: boolean;
  onSubscribe: () => void;
  isSubscribing: boolean;
  canUsePremiumBot: boolean;
  currentCount: number;
  maxCount: number;
  errorMessage: string | null;
}

export const EnhancedBotDetailHeader = ({
  bot,
  isLoading,
  isIntegrated,
  onSubscribe,
  isSubscribing,
  canUsePremiumBot,
  currentCount,
  maxCount,
  errorMessage
}: EnhancedBotDetailHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const getRiskColor = (risk: string | undefined): string => {
    if (!risk) return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    
    switch (risk.toLowerCase()) {
      case 'low':
        return "bg-green-500/20 text-green-500 border-green-500/30";
      case 'medium':
        return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
      case 'high':
        return "bg-red-500/20 text-red-500 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-500 border-gray-500/30";
    }
  };

  if (isLoading) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-muted/10 p-6 md:p-8">
        <div className="space-y-6">
          <div className="flex justify-between items-start">
            <div className="space-y-4 flex-1">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-64" />
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>
            <Skeleton className="h-12 w-32" />
          </div>
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const subscribeButton = (
    <Button 
      className="relative overflow-hidden bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold shadow-lg hover:shadow-primary/25 transition-all duration-300 group"
      size={isMobile ? "sm" : "lg"}
      disabled={isSubscribing || !canUsePremiumBot}
      onClick={onSubscribe}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <span className="relative">
        {t('use_this_bot_with_count', {currentCount, maxCount})}
      </span>
    </Button>
  );

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-muted/10 border border-border/50">
      {/* Background patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-muted/5" />
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-muted/20 to-transparent rounded-full blur-2xl" />
      
      <div className="relative p-6 md:p-8">
        <div className="flex flex-col space-y-6">
          {/* Navigation and Tour */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="space-y-4 flex-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/premium-bots')}
                className="bg-background/80 backdrop-blur-sm border-border/60 hover:bg-background/90 transition-all duration-200"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('back_to_premium_bots')}
              </Button>
              
              {/* Enhanced Bot Title */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur-xl animate-pulse" />
                  <div className="relative w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center border border-primary/20">
                    <Star className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent`}>
                      {bot?.name || t('bot_details')}
                    </h1>
                    <TourButton pageId="premium-bot-detail" className="ml-2" />
                  </div>
                  
                  {/* Enhanced Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={`${isMobile ? "text-xs py-1" : "text-sm py-1"} ${getRiskColor(bot?.risk)} animate-fade-in transition-all duration-300 hover:scale-105`}
                    >
                      <Shield className={`${isMobile ? "mr-1 h-3 w-3" : "mr-1 h-4 w-4"}`} />
                      {bot?.risk ? t(`risk_${bot.risk.toLowerCase()}`) : t('unknown_risk')}
                    </Badge>
                    
                    {bot?.premiumDetails?.subscribers_total && (
                      <Badge variant="outline" className="bg-blue-500/20 text-blue-500 border-blue-500/30 animate-fade-in">
                        <Users className={`${isMobile ? "mr-1 h-3 w-3" : "mr-1 h-4 w-4"}`} />
                        {bot.premiumDetails.subscribers_total} {t('users')}
                      </Badge>
                    )}
                    
                    {bot?.premiumDetails?.potential_profit && (
                      <Badge variant="outline" className="bg-green-500/20 text-green-500 border-green-500/30 animate-fade-in">
                        <TrendingUp className={`${isMobile ? "mr-1 h-3 w-3" : "mr-1 h-4 w-4"}`} />
                        {bot.premiumDetails.potential_profit}% {t('profit')}
                      </Badge>
                    )}
                  </div>
                  
                  <div className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
                    {t('bot_id')}: <span className="font-mono">{bot?.id}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Enhanced Subscribe Button */}
            {!isIntegrated && (
              <div className="relative">
                {!canUsePremiumBot && errorMessage ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {subscribeButton}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{errorMessage}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  subscribeButton
                )}
              </div>
            )}
          </div>
          
          {/* Enhanced Description Card */}
          <div className="relative overflow-hidden rounded-xl bg-background/60 backdrop-blur-sm border border-border/60 p-6 transition-all duration-300 hover:bg-background/80">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-50" />
            <div className="relative">
              <p className={`${isMobile ? "text-sm" : "text-base"} leading-relaxed text-foreground/90`}>
                {bot?.description}
              </p>
              
              {bot?.premiumDetails?.pairs && bot.premiumDetails.pairs.length > 0 && (
                <div className="mt-4 space-y-2">
                  <span className={`${isMobile ? "text-sm" : "text-base"} font-semibold text-foreground`}>
                    {t('supported_pairs')}:
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {bot.premiumDetails.pairs.map((pair, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary" 
                        className={`${isMobile ? "text-xs py-1" : "text-sm py-1"} bg-muted/80 hover:bg-muted transition-colors duration-200`}
                      >
                        {pair}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
